import { Routes, Route } from "react-router-dom";
import { Suspense, lazy } from "react";
import { SiteLayout } from "@/components/layout/SiteLayout";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { RequireAdmin } from "@/components/auth/RequireAdmin";
import { useApplySiteTheme } from "@/hooks/useSiteTheme";

const Home = lazy(() => import("@/pages/Home"));
const About = lazy(() => import("@/pages/About"));
const Shop = lazy(() => import("@/pages/Shop"));
const Product = lazy(() => import("@/pages/Product"));
const Cart = lazy(() => import("@/pages/Cart"));
const Checkout = lazy(() => import("@/pages/Checkout"));
const Payment = lazy(() => import("@/pages/Payment"));
const ThankYou = lazy(() => import("@/pages/ThankYou"));
const Accessibility = lazy(() => import("@/pages/Accessibility"));
const Privacy = lazy(() => import("@/pages/Privacy"));
const Terms = lazy(() => import("@/pages/Terms"));
const NotFound = lazy(() => import("@/pages/NotFound"));
const DynamicPage = lazy(() => import("@/pages/DynamicPage"));
const Contact = lazy(() => import("@/pages/Contact"));
const CancelOrder = lazy(() => import("@/pages/CancelOrder"));
const Tours = lazy(() => import("@/pages/Tours"));

const AdminLogin = lazy(() => import("@/pages/admin/Login"));
const AdminDashboard = lazy(() => import("@/pages/admin/Dashboard"));
const AdminOrders = lazy(() => import("@/pages/admin/Orders"));
const AdminProducts = lazy(() => import("@/pages/admin/Products"));
const AdminContent = lazy(() => import("@/pages/admin/Content"));
const AdminTours = lazy(() => import("@/pages/admin/Tours"));
const AdminCoupons = lazy(() => import("@/pages/admin/Coupons"));
const AdminChecks = lazy(() => import("@/pages/admin/Checks"));
const AdminUsers = lazy(() => import("@/pages/admin/Users"));
const AdminAudit = lazy(() => import("@/pages/admin/Audit"));
const AdminPayments = lazy(() => import("@/pages/admin/Payments"));

export default function App() {
  // Applies the persisted theme (palette / font / button style) to <html> as
  // CSS-vars; re-runs when the admin saves a new theme.
  useApplySiteTheme();
  return (
    <Suspense fallback={<div className="container py-12 text-center">טוען…</div>}>
      <Routes>
        <Route element={<SiteLayout />}>
          <Route path="/" element={<Home />} />
          <Route path="/about" element={<About />} />
          <Route path="/shop" element={<Shop />} />
          <Route path="/wines/:slug" element={<Product />} />
          <Route path="/cart" element={<Cart />} />
          <Route path="/checkout" element={<Checkout />} />
          <Route path="/payment/:publicId" element={<Payment />} />
          <Route path="/thank-you/:publicId" element={<ThankYou />} />
          <Route path="/accessibility" element={<Accessibility />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/cancel-order" element={<CancelOrder />} />
          {/* /tours has a bespoke booking UI rather than the block model. */}
          <Route path="/tours" element={<Tours />} />
          {/* Catch-all single-segment slug → admin-created pages.
              Specific routes above win because React Router prefers static
              segments over `:param` ones, regardless of file order. */}
          <Route path="/:slug" element={<DynamicPage />} />
        </Route>

        <Route path="/admin/login" element={<AdminLogin />} />
        <Route
          element={
            <RequireAdmin>
              <AdminLayout />
            </RequireAdmin>
          }
        >
          <Route path="/admin" element={<AdminDashboard />} />
          <Route path="/admin/orders" element={<AdminOrders />} />
          <Route path="/admin/payments" element={<AdminPayments />} />
          <Route path="/admin/products" element={<AdminProducts />} />
          <Route path="/admin/content" element={<AdminContent />} />
          <Route path="/admin/tours" element={<AdminTours />} />
          <Route path="/admin/coupons" element={<AdminCoupons />} />
          <Route path="/admin/checks" element={<AdminChecks />} />
          <Route path="/admin/users" element={<AdminUsers />} />
          <Route path="/admin/audit" element={<AdminAudit />} />
        </Route>

        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
}
